import { useState, useEffect } from "react"

const createEmitter = () => {
  const subscriptions = new Map()
  return {
    emit: v => subscriptions.forEach(fn => fn(v)),
    subscribe: fn => {
      const key = Symbol()
      subscriptions.set(key, fn)
      return () => subscriptions.delete(key)
    }
  }
}

/**
 * createStore - Manage state across components without Context API
 * This is a modification of zustand (https://github.com/pmndrs/zustand)
 * gotten from https://formidable.com/blog/2021/stores-no-context-api/
 * We may decide in the future to use zustand directly but we start here
 * @param initialValue {any} - Initial value to be stored
 * @param update {function} - An optional callback to be called in the setter function
 * @example useStore = createStore({ value: 0 }, updatedValue => updatedValue + 1)
 * Please note you must provide an update function
 * @returns {{ useStore: function, set: function }} - an object returning a hook (returning the store value) and a setter function
 */
export const createStore = (initialValue, update) => {
  const emitter = createEmitter()

  let store = initialValue
  const get = () => store
  const set = newValue => {
    store = newValue
    if (update) update(newValue)
    // notify all subscriptions when the store updates
    emitter.emit(newValue)
  }

  // Beautiful. Our store now has a way to get
  // and set itself when we initialize it
  // store = init(set, get)

  const useStore = () => {
    // initialize component with latest store
    const [localStore, setLocalStore] = useState(get())
    // update our local store when the global
    // store updates.
    //
    // emitter.subscribe returns a cleanup
    // function, so react will clean this
    // up on unmount.
    useEffect(() => emitter.subscribe(setLocalStore), [])
    return localStore
  }

  return { useStore, set }
}
